// Quanto se espera receber de uma UC, mês a mês, por data de vencimento.
//
// Vive em `_shared/` pelo mesmo motivo que `splitParceira.ts`: isto é dinheiro,
// e duas cópias da regra divergiriam na primeira mudança de deságio. O front
// reexporta por `src/lib/recebiveisUc.ts`.
//
// ─────────────────────────────────────────────────────────────────────────────
// O QUE ESTE MÓDULO NÃO FAZ, DE PROPÓSITO
//
// A demanda pedia "projetar, por data de vencimento, quanto se espera receber
// de cada UC". Três dos quatro insumos que isso pressupõe NÃO são fatos:
//
//   1. TARIFA. `unidades_consumidoras.valor_tarifa` está vazio em 75% das UCs
//      ativas (medido em produção, 28/08/2026) e não é a tarifa de faturamento
//      — quem cobra é `faturas.valor_tarifa_distribuidora`, que só existe
//      DEPOIS que a fatura chega. Para um mês futuro a tarifa é herdada da
//      última fatura conhecida (`resolverTarifaVigente`).
//
//   2. VENCIMENTO. Não existe "vencimento configurado por UC": o boleto segue
//      `fatura.vencimento_boleto || fatura.data_vencimento`, ou seja, a fatura
//      da distribuidora. Medido em produção sobre 6.627 faturas limpas: só
//      18% das UCs repetem o mesmo dia, e prever pela moda acerta o dia exato
//      em 30% dos casos, ±2 dias em 56%, ±5 dias em 89%. Por isso este módulo
//      devolve uma JANELA, nunca uma data única com cara de fato.
//
//   3. ENERGIA. O boleto cobra a energia INJETADA (o que o rateio mandou), não
//      o consumo da UC — ver `calculoBoletoVigor.ts`. O consumo projetado é o
//      melhor proxy disponível, mas é um proxy, e o módulo diz isso.
//
// Só o DESCONTO é fato: vem do cadastro da UC e está preenchido em 94% das
// ativas.
//
// A consequência de projeto: nada aqui devolve um número liso. Cada mês carrega
// `origem` ('emitido' vs 'projetado') e `confianca`, porque o Financeiro trata
// número único como fato — foi o erro que quase cometemos na demanda anterior.
//
// ─────────────────────────────────────────────────────────────────────────────
// Módulo PURO — sem rede, sem Supabase, sem React.
//   node --test supabase/functions/_shared/__tests__/recebivelUc.test.ts

import { mesReferenciaParaOrdinal, normalizarMesReferencia, rotuloDeOrdinal } from './consumoObservado.ts';

// ─────────────────────────── tipos de entrada ───────────────────────────

/** Uma fatura como sai do banco, no que interessa a recebível. */
export interface FaturaRecebivel {
  mes_referencia?: string | null;
  data_vencimento?: string | null;
  vencimento_boleto?: string | null;
  status_boleto_vigor?: string | null;
  valor_boleto_vigor?: number | string | null;
  valor_tarifa_distribuidora?: number | string | null;
  tarifa_bandeira?: number | string | null;
  desconto_tarifa?: number | string | null;
  desconto_bandeira?: number | string | null;
  qtd_injecao_scee_boleto?: number | string | null;
  qtd_adc_bandeira_boleto?: number | string | null;
  /** Consumo da UC no mês. Não entra no valor — alimenta a projeção de consumo. */
  consumo_ativo_kwh?: number | string | null;
  ambiente?: string | null;
  // Não entram em nenhuma conta daqui. Existem porque o front deriva o status
  // real do boleto antes de montar a agenda (`src/lib/recebiveisUc.ts`,
  // `normalizarStatus`) e precisa carregá-los junto com a fatura.
  status_fatura?: string | null;
  data_pgto_boleto?: string | null;
  quitado_em?: string | null;
  energia_injetada_gd1_kwh?: number | string | null;
  energia_injetada_gd2_kwh?: number | string | null;
  // Boleto único (UC de empresa parceira) — ver `valorSemEquatorial`.
  valor_emissao_boleto_unico?: number | string | null;
  valor_fatura_distribuidora?: number | string | null;
  equatorial_quitada_pelo_cliente?: boolean | null;
  debito_automatico_equatorial?: boolean | null;
  empresa_parceira_id?: string | null;
}

const num = (v: unknown): number => {
  const n = Number(v ?? 0);
  return Number.isFinite(n) ? n : 0;
};

const round2 = (v: number): number => Math.round(v * 100) / 100;

/**
 * Faturas que NÃO descrevem o ciclo mensal e por isso não servem para inferir
 * cadência nem tarifa.
 *
 * `Parcelado` é o caso que mais engana: o acordo gera boletos com vencimento
 * fixo (dia 15, por exemplo) espalhados até 2027, e eles fazem uma UC de
 * vencimento caótico parecer perfeitamente regular. Medido em produção: a UC
 * 10040269661 tem 19 faturas em 12 meses por causa disso.
 */
const STATUS_FORA_DO_CICLO = new Set(['Parcelado', 'Cancelado']);

/** Descarta sandbox, cancelada, parcelamento e linha sem mês de referência. */
export function faturasDoCiclo(faturas: readonly FaturaRecebivel[]): FaturaRecebivel[] {
  return faturas.filter(
    (f) =>
      f.ambiente !== 'sandbox' &&
      !STATUS_FORA_DO_CICLO.has(String(f.status_boleto_vigor ?? '')) &&
      mesReferenciaParaOrdinal(f.mes_referencia) != null,
  );
}

/** A data que vale para o consumidor: o vencimento do boleto, com a fatura como reserva. */
export function vencimentoEfetivo(f: FaturaRecebivel): string | null {
  const v = f.vencimento_boleto || f.data_vencimento;
  if (!v) return null;
  const iso = String(v).slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(iso) ? iso : null;
}

/**
 * O que o boleto cobra FORA a conta da Equatorial.
 *
 * No boleto único (hoje só as 77 UCs da Gran) o consumidor paga num boleto só
 * a parte da Vigor/parceira e a fatura da Equatorial:
 * `valor_emissao_boleto_unico = valor_boleto_vigor + valor_fatura_distribuidora`
 * (`BoletoEmissionModal.tsx`). A regra do dono do produto (15/09/2026) é
 * **total do boleto − Equatorial**, e só depois o percentual da parceira.
 *
 * Por que não ler `valor_boleto_vigor` direto: medido nos boletos da Gran
 * vencendo de SET/2026 em diante, 7 deles têm `valor_boleto_vigor` IGUAL à
 * conta da Equatorial (R$ 28,7 mil, zero kWh injetado) e 18 têm o campo zerado
 * com o boleto inteiro sendo Equatorial. A subtração dá R$ 0 nos 25 — que é a
 * verdade: sem injeção não há receita — e bate centavo a centavo com
 * `valor_boleto_vigor` nos 42 boletos normais.
 *
 * Exceção: se a Equatorial ficou FORA do boleto (cliente já quitou ou está em
 * débito automático) e houve injeção, o total já é só a parte da Vigor —
 * subtrair tiraria dinheiro que nunca esteve no boleto.
 *
 * `boletoUnico = false` devolve `valor_boleto_vigor`: nos boletos comuns da
 * Vigor `valor_emissao_boleto_unico` é só uma cópia dele (1.315 casos em
 * AGO–OUT/2026), e subtrair a Equatorial ali derrubaria boleto normal.
 */
export function valorSemEquatorial(f: FaturaRecebivel, boletoUnico: boolean): number {
  const vigor = num(f.valor_boleto_vigor);
  const total = num(f.valor_emissao_boleto_unico);
  if (!boletoUnico || total <= 0) return vigor;
  const equatorialFora = f.equatorial_quitada_pelo_cliente === true || f.debito_automatico_equatorial === true;
  if (equatorialFora && num(f.qtd_injecao_scee_boleto) > 0) return total;
  return Math.max(0, total - num(f.valor_fatura_distribuidora));
}

/**
 * Uma fatura tem conteúdo quando cobra algo ou ao menos registra energia.
 *
 * A base tem linhas-fantasma: mesma competência de uma fatura real, mas com
 * `valor_boleto_vigor = 0`, `valor_tarifa_distribuidora = 0` e `status` nulo
 * (visto nas UCs 256379501203 e 386209501207). Deixar uma dessas ganhar a
 * dedupe faz a competência valer R$ 0 — um recebível some sem ninguém ver, que
 * é exatamente o que `INV-01` proíbe.
 */
export function temConteudo(f: FaturaRecebivel): boolean {
  return num(f.valor_boleto_vigor) > 0 || num(f.qtd_injecao_scee_boleto) > 0;
}

/**
 * Uma fatura por competência: a que tem conteúdo vence a vazia; entre duas
 * equivalentes, a de vencimento mais recente.
 */
export function porCompetencia(faturas: readonly FaturaRecebivel[]): Map<number, FaturaRecebivel> {
  const mapa = new Map<number, FaturaRecebivel>();
  for (const f of faturas) {
    const ord = mesReferenciaParaOrdinal(f.mes_referencia);
    if (ord == null) continue;
    const atual = mapa.get(ord);
    if (!atual) {
      mapa.set(ord, f);
      continue;
    }
    const novaTem = temConteudo(f);
    const atualTem = temConteudo(atual);
    if (novaTem !== atualTem) {
      if (novaTem) mapa.set(ord, f);
      continue;
    }
    // Determinístico, como a dedupe de consumoObservado (que fica com o MAIOR consumo).
    if (String(vencimentoEfetivo(f) ?? '') > String(vencimentoEfetivo(atual) ?? '')) mapa.set(ord, f);
  }
  return mapa;
}

// ─────────────────────────── tarifa vigente ───────────────────────────

export type ConfiancaRecebivel = 'fato' | 'alta' | 'media' | 'baixa';

export interface TarifaVigente {
  /** R$/kWh cheia da distribuidora. 0 = desconhecida. */
  tarifa: number;
  /** R$/kWh de bandeira. 0 é legítimo (bandeira verde). */
  bandeira: number;
  /** Competência da fatura de onde veio, para o operador conferir. */
  competencia: string | null;
  /** Quantos meses atrás. Acima de `MAX_MESES_TARIFA` a tarifa é velha demais. */
  mesesAtras: number | null;
  confianca: ConfiancaRecebivel;
  aviso: string | null;
}

/**
 * Acima disto a tarifa herdada não descreve mais o presente: a Equatorial
 * reajusta anualmente e revisa extraordinariamente, então meio ano já é muito.
 */
export const MAX_MESES_TARIFA = 6;

/**
 * A tarifa vigente é a da fatura mais recente que a tenha gravada.
 *
 * Não usa `unidades_consumidoras.valor_tarifa`: medido em produção, está vazio
 * em 75,3% das UCs ativas e é um campo de origem CRM, não de faturamento.
 */
export function resolverTarifaVigente(
  faturas: readonly FaturaRecebivel[],
  competenciaAtualOrdinal: number,
): TarifaVigente {
  const comTarifa = faturasDoCiclo(faturas)
    .filter((f) => num(f.valor_tarifa_distribuidora) > 0)
    .sort((a, b) => (mesReferenciaParaOrdinal(b.mes_referencia) ?? 0) - (mesReferenciaParaOrdinal(a.mes_referencia) ?? 0));

  const recente = comTarifa[0];
  if (!recente) {
    return {
      tarifa: 0,
      bandeira: 0,
      competencia: null,
      mesesAtras: null,
      confianca: 'baixa',
      aviso: 'Nenhuma fatura desta UC tem a tarifa da distribuidora gravada — não dá para estimar o valor.',
    };
  }

  const ord = mesReferenciaParaOrdinal(recente.mes_referencia)!;
  const mesesAtras = competenciaAtualOrdinal - ord;
  const tarifa = num(recente.valor_tarifa_distribuidora);
  const bandeira = num(recente.tarifa_bandeira);

  if (mesesAtras > MAX_MESES_TARIFA) {
    return {
      tarifa,
      bandeira,
      competencia: rotuloDeOrdinal(ord),
      mesesAtras,
      confianca: 'baixa',
      aviso:
        `A tarifa mais recente desta UC é de ${rotuloDeOrdinal(ord)}, ${mesesAtras} meses atrás. ` +
        'Houve reajuste da distribuidora desde então — o valor estimado está defasado.',
    };
  }

  return {
    tarifa,
    bandeira,
    competencia: rotuloDeOrdinal(ord),
    mesesAtras,
    confianca: mesesAtras <= 2 ? 'alta' : 'media',
    aviso:
      mesesAtras <= 2
        ? null
        : `Tarifa herdada de ${rotuloDeOrdinal(ord)} (${mesesAtras} meses atrás).`,
  };
}

// ─────────────────────────── vencimento inferido ───────────────────────────

export interface VencimentoInferido {
  /** Dia do mês mais frequente. `null` quando não há base. */
  dia: number | null;
  /**
   * Quantos meses depois da competência o boleto vence. Medido em produção:
   * +1 em 68,4% das faturas e 0 em 23,7% — ou seja, usar a própria competência
   * como mês de vencimento erraria o mês em dois terços dos casos.
   */
  lagMeses: number;
  /** Fração das competências que caíram exatamente nesse dia. */
  aderencia: number;
  /** Fração das competências que seguiram `lagMeses`. */
  aderenciaLag: number;
  /** Faixa observada — é isto que deve ser mostrado, não `dia` sozinho. */
  janela: { min: number; max: number } | null;
  /** Quantas competências entraram. */
  meses: number;
  /**
   * Confiança do DIA. O mês é bem mais previsível que o dia (90,4% contra
   * 32,7% no backtest de produção) — por isso `confiancaMes` é separada: um
   * agregado mensal pode confiar no mês mesmo quando o dia é um chute.
   */
  confianca: ConfiancaRecebivel;
  confiancaMes: ConfiancaRecebivel;
  aviso: string | null;
}

/** Defasagem competência → vencimento mais comum na base, usada quando a UC não tem histórico. */
export const LAG_VENCIMENTO_PADRAO = 1;

/**
 * Abaixo disto não há cadência para ler — mesmo limiar do consumo observado
 * (`MESES_MINIMOS_OPERACIONAL`), pela mesma razão: dois pontos não são um padrão.
 */
export const MESES_MINIMOS_VENCIMENTO = 3;

/** Acima desta aderência o dia se repete o suficiente para ser chamado de previsível. */
export const ADERENCIA_VENCIMENTO_ALTA = 0.8;

/**
 * Infere o dia do vencimento pela moda das faturas do ciclo.
 *
 * Medido em produção (28/08/2026, 6.627 faturas limpas de 1.217 UCs): a moda
 * acerta o dia exato em 30% dos casos, ±2 dias em 56% e ±5 dias em 89%. É por
 * isso que o retorno traz `janela` e a confiança quase nunca é 'alta' — a data
 * não é um fato do cadastro, é um hábito da distribuidora, e ela muda o ciclo
 * de leitura sem avisar.
 */
export function inferirVencimento(faturas: readonly FaturaRecebivel[]): VencimentoInferido {
  const entradas = [...porCompetencia(faturasDoCiclo(faturas)).entries()];
  const dias = entradas
    .map(([, f]) => {
      const v = vencimentoEfetivo(f);
      return v ? Number(v.slice(8, 10)) : 0;
    })
    .filter((d) => d >= 1 && d <= 31);

  // A defasagem competência → mês de vencimento, medida na própria UC.
  const lags = entradas
    .map(([ord, f]) => {
      const v = vencimentoEfetivo(f);
      return v ? ordinalDaData(v) - ord : null;
    })
    .filter((l): l is number => l != null && l >= 0 && l <= 3);
  let lagMeses = LAG_VENCIMENTO_PADRAO;
  let aderenciaLag = 0;
  if (lags.length > 0) {
    const cl = new Map<number, number>();
    for (const l of lags) cl.set(l, (cl.get(l) ?? 0) + 1);
    const [melhor, n] = [...cl.entries()].sort((a, b) => b[1] - a[1] || a[0] - b[0])[0];
    lagMeses = melhor;
    aderenciaLag = n / lags.length;
  }
  const confiancaMes: ConfiancaRecebivel =
    lags.length < MESES_MINIMOS_VENCIMENTO ? 'baixa' : aderenciaLag >= 0.8 ? 'alta' : 'media';

  if (dias.length < MESES_MINIMOS_VENCIMENTO) {
    return {
      dia: null,
      lagMeses,
      aderencia: 0,
      aderenciaLag,
      janela: null,
      meses: dias.length,
      confianca: 'baixa',
      confiancaMes,
      aviso:
        dias.length === 0
          ? 'Esta UC ainda não tem fatura com vencimento — não há cadência para estimar a data.'
          : `Só ${dias.length} ${dias.length === 1 ? 'mês' : 'meses'} de vencimento conhecido; ` +
            `são necessários ${MESES_MINIMOS_VENCIMENTO} para estimar a data.`,
    };
  }

  const contagem = new Map<number, number>();
  for (const d of dias) contagem.set(d, (contagem.get(d) ?? 0) + 1);
  // Empate resolvido pelo dia MENOR só para manter a função determinística; na
  // prática um empate nunca sobrevive ao corte de aderência abaixo (dois dias
  // empatados dão no máximo 50%), então quem decide o dia nesses casos é a
  // mediana, mais adiante.
  const [moda, vezes] = [...contagem.entries()].sort((a, b) => b[1] - a[1] || a[0] - b[0])[0];

  const aderencia = vezes / dias.length;
  const janela = { min: Math.min(...dias), max: Math.max(...dias) };
  const amplitude = janela.max - janela.min;

  if (aderencia >= ADERENCIA_VENCIMENTO_ALTA) {
    return { dia: moda, lagMeses, aderencia, aderenciaLag, janela, meses: dias.length, confianca: 'alta', confiancaMes, aviso: null };
  }

  // Sem um dia dominante, a moda deixa de ser um bom centro: com dias 9,10,11,12
  // toda contagem é 1 e o desempate escolheria o 9, uma ponta da faixa. A
  // MEDIANA é o centro robusto — e, medida contra as faturas de produção, erra
  // menos que a moda justamente nas UCs de cadência irregular, que são 70% delas.
  const ordenados = [...dias].sort((a, b) => a - b);
  const meio = Math.floor(ordenados.length / 2);
  const dia =
    ordenados.length % 2 === 1 ? ordenados[meio] : Math.floor((ordenados[meio - 1] + ordenados[meio]) / 2);

  const dentroDe2 = dias.filter((d) => Math.abs(d - dia) <= 2).length / dias.length;
  if (dentroDe2 >= ADERENCIA_VENCIMENTO_ALTA) {
    return {
      dia,
      lagMeses,
      aderencia,
      aderenciaLag,
      janela,
      meses: dias.length,
      confianca: 'media',
      confiancaMes,
      aviso: `O vencimento varia em torno do dia ${dia} (entre ${janela.min} e ${janela.max}).`,
    };
  }

  return {
    dia,
    lagMeses,
    aderencia,
    aderenciaLag,
    janela,
    meses: dias.length,
    confianca: 'baixa',
    confiancaMes,
    aviso:
      `O vencimento desta UC não segue um dia fixo — nos últimos ${dias.length} meses caiu entre ` +
      `o dia ${janela.min} e o dia ${janela.max}${amplitude >= 15 ? ', variando mais de duas semanas' : ''}. ` +
      'Trate a data como aproximada.',
  };
}

/** Constrói a data ISO de uma competência, respeitando meses curtos. */
export function dataDoVencimento(ordinal: number, dia: number): string {
  const ano = Math.floor(ordinal / 12);
  const mes = ((ordinal % 12) + 12) % 12; // 0-11
  const ultimoDia = new Date(Date.UTC(ano, mes + 1, 0)).getUTCDate();
  const d = Math.min(Math.max(dia, 1), ultimoDia);
  return `${ano}-${String(mes + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

// ─────────────────────────── desconto vigente ───────────────────────────

export interface DescontoVigente {
  tarifa: number;
  bandeira: number;
  origem: 'fatura' | 'cadastro' | 'padrao';
  aviso: string | null;
}

/**
 * Defaults que o próprio sistema aplica quando a UC não tem desconto gravado
 * — ver `FaturaCalculationModal.tsx`, que além de usá-los grava de volta na UC.
 * Repetidos aqui para a projeção não divergir da emissão.
 */
export const DESCONTO_PADRAO_TARIFA = 0.2;
export const DESCONTO_PADRAO_BANDEIRA = 0.5;

/**
 * O desconto que vale para frente.
 *
 * A fatura manda sobre o cadastro: medido em produção, os dois batem em 95,8%
 * das faturas de 2026, e quase toda divergência é UC com desconto zerado no
 * cadastro cuja fatura cobrou 20%. Confiar no cadastro nesses casos projetaria
 * o boleto 25% acima do que sai de verdade.
 */
export function resolverDescontoVigente(
  faturas: readonly FaturaRecebivel[],
  cadastro: { descontoTarifa?: number | null; descontoBandeira?: number | null },
): DescontoVigente {
  const recente = faturasDoCiclo(faturas)
    .filter((f) => num(f.desconto_tarifa) > 0)
    .sort((a, b) => (mesReferenciaParaOrdinal(b.mes_referencia) ?? 0) - (mesReferenciaParaOrdinal(a.mes_referencia) ?? 0))[0];

  if (recente) {
    return {
      tarifa: num(recente.desconto_tarifa),
      bandeira: num(recente.desconto_bandeira),
      origem: 'fatura',
      aviso: null,
    };
  }

  const dc = num(cadastro.descontoTarifa);
  if (dc > 0) {
    return { tarifa: dc, bandeira: num(cadastro.descontoBandeira), origem: 'cadastro', aviso: null };
  }

  return {
    tarifa: DESCONTO_PADRAO_TARIFA,
    bandeira: DESCONTO_PADRAO_BANDEIRA,
    origem: 'padrao',
    aviso:
      'Esta UC não tem desconto no cadastro nem em fatura anterior — a estimativa usa o padrão ' +
      `de ${DESCONTO_PADRAO_TARIFA * 100}% na tarifa. Confira o contrato.`,
  };
}

// ─────────────────────── abatimentos da fatura ───────────────────────

/**
 * Quanto do boleto NÃO é explicado por `kWh × tarifa × (1 − desconto)`.
 *
 * O boleto real ainda desconta restituição de imposto, energia não isenta e
 * parcela injetada sem desconto, e soma juros/multas/parcelamentos — ver
 * `calculoBoletoVigor.ts`. Nenhum deles é conhecido antes da fatura chegar.
 *
 * Medido sobre 2.923 faturas de 2026: a fórmula simplificada superestima o
 * boleto em 6,5% na mediana (p25 2,7%, p75 7,8%, p90 9,0%) — restituição de
 * imposto sozinha vale −3,2% do total. Ignorar isso não é ruído: é um viés
 * sistemático numa direção só, que infla todo agregado do Painel.
 *
 * A correção é o fator observado na PRÓPRIA UC, porque a composição varia com
 * o perfil tributário do cliente. Sem histórico, cai na mediana da base.
 *
 * E é a média dos DOIS últimos meses, não a mediana de todos: a razão cai mês a
 * mês dentro da mesma UC (0,974 → 0,974 → 0,939 → 0,917 na UC 290541101242),
 * porque restituição e energia não isenta crescem como fatia do boleto. A
 * mediana de uma série com tendência fica para trás — foi ela que produzia um
 * viés de +3,1%. Backtest sobre 442 UCs: mediana 3,14% de viés contra 0,09% da
 * média dos dois últimos.
 */
export const FATOR_ABATIMENTO_PADRAO = 0.935;

/** Quantos meses recentes entram no fator. Dois: um só é ruidoso, muitos ficam para trás. */
export const MESES_FATOR_ABATIMENTO = 2;

/** Limites de sanidade: fora disto o fator vira ruído de fatura errada, não perfil. */
export const LIMITE_FATOR_ABATIMENTO = { min: 0.75, max: 1.1 } as const;

export interface FatorAbatimento {
  fator: number;
  origem: 'uc' | 'padrao';
  meses: number;
  /**
   * Competências cujo boleto não bate com a própria energia e tarifa da fatura.
   *
   * Não é ruído de arredondamento: na UC 256379501203 o boleto de JUL/2026 saiu
   * R$ 680,71 para 18.440 kWh (R$ 0,037/kWh) quando o mês anterior cobrou
   * R$ 1,22/kWh. Duas UCs assim respondem por 94% do desvio agregado da
   * carteira — some-as no total do Painel e ele mente 6%.
   *
   * Marcadas, nunca descartadas: a UC segue projetando (a lição da demanda
   * anterior é que a UC "estranha" costuma ter uso legítimo), mas quem olha o
   * agregado precisa saber que ali há fatura a conferir.
   */
  competenciasInconsistentes: string[];
}

/**
 * Razão mediana entre o boleto que saiu e o que a fórmula simplificada previa,
 * calculada nas faturas da própria UC.
 */
export function calcularFatorAbatimento(faturas: readonly FaturaRecebivel[]): FatorAbatimento {
  // Em ordem CRONOLÓGICA: o fator olha para os meses recentes, então a ordem importa.
  const razoes: number[] = [];
  const competenciasInconsistentes: string[] = [];
  const ordenadas = [...porCompetencia(faturasDoCiclo(faturas)).entries()].sort((a, b) => a[0] - b[0]);
  for (const [ord, f] of ordenadas) {
    const kwh = num(f.qtd_injecao_scee_boleto);
    const tarifa = num(f.valor_tarifa_distribuidora);
    const real = num(f.valor_boleto_vigor);
    if (kwh <= 0 || tarifa <= 0 || real <= 0) continue;
    const simples =
      kwh * tarifa * (1 - num(f.desconto_tarifa)) +
      num(f.qtd_adc_bandeira_boleto) * num(f.tarifa_bandeira) * (1 - num(f.desconto_bandeira));
    if (simples <= 0) continue;
    const r = real / simples;
    if (r >= LIMITE_FATOR_ABATIMENTO.min && r <= LIMITE_FATOR_ABATIMENTO.max) razoes.push(r);
    else competenciasInconsistentes.push(rotuloDeOrdinal(ord));
  }

  if (razoes.length < MESES_MINIMOS_VENCIMENTO) {
    return {
      fator: FATOR_ABATIMENTO_PADRAO,
      origem: 'padrao',
      meses: razoes.length,
      competenciasInconsistentes,
    };
  }
  const recentes = razoes.slice(-MESES_FATOR_ABATIMENTO);
  const fator = recentes.reduce((s, r) => s + r, 0) / recentes.length;
  return { fator, origem: 'uc', meses: razoes.length, competenciasInconsistentes };
}

// ─────────────────────── faturamento comprovado ───────────────────────

/**
 * Quantas competências com cobrança real a UC precisa ter para que a projeção
 * dela conte como receita esperada.
 *
 * Era 2 até 15/09/2026. Medido em produção: das UCs não canceladas do rateio
 * de AGO/2026, 41 já tinham recebido o primeiro boleto (R$ 78 mil/mês) e mesmo
 * assim a projeção delas caía em "em entrada", fora do total. Quem já foi
 * cobrado uma vez fatura — o que separa carteira futura de caixa é nunca ter
 * sido cobrado, não ter sido cobrado só uma vez.
 */
export const COMPETENCIAS_MINIMAS_FATURADAS = 1;

/**
 * A UC já demonstrou que fatura?
 *
 * Ter consumo não é ter receita. Medido em produção (29/08/2026), 159 UCs
 * projetavam R$ 302.254 para setembro sem NUNCA terem sido cobradas: 66 em
 * 1º Ciclo, 76 com situação em branco, e as faturas delas são 137 "Sem
 * injeção" + 130 "Sem cobrança" — a Vigor ainda não injeta energia para
 * esses clientes. Somar isso ao "quanto vou receber" do CEO responde a
 * pergunta errada: é carteira futura, não caixa do mês que vem.
 *
 * Era 47% do total projetado de setembro. Sem separar, o número saía
 * R$ 854 mil contra um faturamento real que roda em R$ 620 mil/mês.
 *
 * Separado, nunca escondido: vira um terceiro balde (`emEntrada`), visível
 * como potencial, fora do total que responde a pergunta. Basta uma competência
 * cobrada para sair dele — ver `COMPETENCIAS_MINIMAS_FATURADAS`.
 */
export function temFaturamentoComprovado(faturas: readonly FaturaRecebivel[]): boolean {
  const comCobranca = new Set<number>();
  for (const [ord, f] of porCompetencia(faturasDoCiclo(faturas))) {
    if (num(f.valor_boleto_vigor) > 0) comCobranca.add(ord);
  }
  return comCobranca.size >= COMPETENCIAS_MINIMAS_FATURADAS;
}

// ─────────────────────── saldo pendente de UC cancelada ───────────────────────

/**
 * Fração do mês anterior que uma UC cancelada ainda fatura no mês seguinte.
 *
 * Cancelada não é UC morta: ela ainda compensa o saldo de créditos (e, até sair
 * do rateio, uma ou duas injeções novas), e esse saldo é cobrado. Medido em
 * produção (15/09/2026) nas UCs com cadastro "Cancelada": o faturamento do
 * grupo rodou em R$ 109 mil (JUN), R$ 84 mil (JUL) e R$ 105 mil (AGO), e uma UC
 * cancelada cobrada num mês faturou no mês seguinte 62% (JUN→JUL) e 75%
 * (JUL→AGO) do valor anterior.
 *
 * O saldo acumulado da fatura NÃO serve de teto: em 32 de 69 UCs a injeção de
 * AGO passou do saldo de JUL, porque a UC ainda estava no rateio.
 */
export const RETENCAO_MENSAL_CANCELADA = 0.7;

/**
 * Sem cobrança há mais que isto, a UC cancelada saiu de vez: a fatura parou de
 * chegar. Dois meses cobrem o atraso normal de chegada da fatura.
 */
export const MESES_MAXIMOS_SEM_COBRANCA_CANCELADA = 2;

/**
 * kWh esperado por competência de uma UC cancelada, decaindo mês a mês.
 *
 * Parte da ÚLTIMA competência cobrada (com kWh injetado) e aplica
 * `RETENCAO_MENSAL_CANCELADA` a cada mês seguinte. Não projeta nada quando:
 *  - chegou fatura depois da última cobrada, e zerada — o saldo acabou;
 *  - a última cobrança é antiga demais — a fatura parou de chegar.
 *
 * O resultado substitui a projeção de consumo: consumo descreve quem continua
 * recebendo energia, e cancelada só gasta o que sobrou.
 */
export function projecaoSaldoCancelada(
  faturas: readonly FaturaRecebivel[],
  competenciaAtualOrdinal: number,
  horizonteMeses: number,
): Map<number, number> {
  const projecao = new Map<number, number>();
  const ciclo = [...porCompetencia(faturasDoCiclo(faturas)).entries()].sort((a, b) => a[0] - b[0]);
  const cobradas = ciclo.filter(([, f]) => num(f.valor_boleto_vigor) > 0 && num(f.qtd_injecao_scee_boleto) > 0);
  const ultima = cobradas[cobradas.length - 1];
  if (!ultima) return projecao;

  const [ordUltima, fUltima] = ultima;
  if (ciclo[ciclo.length - 1][0] > ordUltima) return projecao;
  if (ordUltima < competenciaAtualOrdinal - MESES_MAXIMOS_SEM_COBRANCA_CANCELADA) return projecao;

  // Mesmo fim de horizonte da projeção de consumo em `montarAgendaDaUc`.
  const ultimoOrdinal = competenciaAtualOrdinal - 1 + horizonteMeses;
  const kwh = num(fUltima.qtd_injecao_scee_boleto);
  for (let i = 1; ordUltima + i <= ultimoOrdinal; i++) {
    projecao.set(ordUltima + i, Math.round(kwh * RETENCAO_MENSAL_CANCELADA ** i * 100) / 100);
  }
  return projecao;
}

// ─────────────────────────── valor esperado ───────────────────────────

export interface ValorRecebivel {
  /** O que o consumidor paga — a conferência operacional olha para este. */
  valorBoleto: number;
  /** O que entra na Vigor. Difere do boleto só em UC de empresa parceira. */
  valorVigor: number;
}

/**
 * Converte energia em dinheiro, nos dois sentidos que a demanda nomeia.
 *
 * Para UC própria da Vigor os dois valores coincidem: quem emite é a Vigor e o
 * boleto inteiro é dela.
 *
 * Para UC de empresa parceira a Vigor fica com `percentual_vigor` do boleto sem
 * a Equatorial — hoje 65%, os outros 35% vão para a Gran. Regra definida pelo
 * dono do produto em 15/09/2026 para a previsão de caixa: "total do boleto,
 * menos a Equatorial, menos 35%". Substituiu `kWh × tarifa cheia × 65%` de
 * `splitParceira.ts`, que dá mais (a tarifa cheia ignora o desconto ao
 * consumidor) e por isso não descreve o que a Vigor espera receber no painel.
 * O split registrado no gateway continua com a regra dele.
 */
export function calcularValorRecebivel({
  kwh,
  tarifa,
  bandeira,
  descontoTarifa,
  descontoBandeira,
  percentualVigor,
  fatorAbatimento = 1,
}: {
  kwh: number;
  tarifa: number;
  bandeira: number;
  descontoTarifa: number;
  descontoBandeira: number;
  /** `empresas_parceiras.percentual_vigor` (65 = deságio de 35%). Ausente = UC da Vigor. */
  percentualVigor?: number | null;
  /** Correção dos componentes que só existem na fatura — ver `calcularFatorAbatimento`. */
  fatorAbatimento?: number;
}): ValorRecebivel {
  const energiaCliente = kwh * tarifa * (1 - descontoTarifa);
  const bandeiraCliente = kwh * bandeira * (1 - descontoBandeira);
  const valorBoleto = round2((energiaCliente + bandeiraCliente) * fatorAbatimento);

  return { valorBoleto, valorVigor: parteDaVigor(valorBoleto, percentualVigor) };
}

/** A fatia da Vigor num valor de boleto já sem a Equatorial. Sem parceira, é tudo. */
export function parteDaVigor(valorSemEquatorialDoBoleto: number, percentualVigor?: number | null): number {
  const fatorVigor = num(percentualVigor) / 100;
  if (fatorVigor <= 0) return round2(valorSemEquatorialDoBoleto);
  return round2(valorSemEquatorialDoBoleto * Math.min(fatorVigor, 1));
}

// ─────────────────────────── agenda mês a mês ───────────────────────────

export type OrigemRecebivel = 'emitido' | 'projetado';

export interface RecebivelMes {
  /** 'SET/2026' — o mês de CONSUMO. */
  competencia: string;
  ordinal: number;
  origem: OrigemRecebivel;
  /** Data ISO. Em 'projetado' é estimada — leia `vencimentoEstimado`. */
  vencimento: string | null;
  /**
   * Ordinal do MÊS em que o dinheiro cai — o eixo de "por data de vencimento".
   * Difere de `ordinal` em 68% dos casos (a fatura vence no mês seguinte ao
   * consumo), e é por ele que qualquer agregado mensal de caixa deve agrupar.
   */
  ordinalVencimento: number | null;
  vencimentoEstimado: boolean;
  /** Faixa de dias observada, quando a data é estimada. */
  janelaDias: { min: number; max: number } | null;
  valorBoleto: number;
  valorVigor: number;
  /** kWh que gerou o valor. Em 'emitido' é o que a fatura cobrou. */
  kwh: number;
  /**
   * A pior das confianças — conservadora de propósito, para quem só quer um
   * sinal. Quem exibe deve preferir `confiancaValor`/`confiancaData`.
   */
  confianca: ConfiancaRecebivel;
  /**
   * Confiança no VALOR: depende só de tarifa e consumo projetado.
   *
   * Separada porque o dia do vencimento não altera QUANTO se recebe, só QUANDO.
   * Misturar as duas foi um erro de desenho real: com a confiança única, 3.697
   * das 4.338 linhas projetadas da carteira saíam como "frágil" e nenhuma como
   * "firme" — um rótulo que, aplicado a quase tudo, não distingue nada.
   */
  confiancaValor: ConfiancaRecebivel;
  /**
   * Confiança na DATA: o dia exato. Medido em produção, o dia acerta em 32,7%
   * e o mês em 90,4%, então esta é quase sempre a metade fraca.
   */
  confiancaData: ConfiancaRecebivel;
  /**
   * Confiança no MÊS em que o dinheiro cai — o que de fato importa para um
   * agregado mensal de caixa, já que é `ordinalVencimento` que define o balde.
   */
  confiancaMes: ConfiancaRecebivel;
  /** Status do boleto, quando já emitido — 'Em aberto', 'Pago', 'Vencido'. */
  statusBoleto: string | null;
  /**
   * A UC já demonstrou que fatura. `false` = cliente em entrada (1º ciclo, sem
   * injeção ainda): é carteira futura, não caixa do mês. Ver
   * `temFaturamentoComprovado`.
   */
  faturamentoComprovado: boolean;
  avisos: string[];
}

export interface AgendaRecebiveis {
  meses: RecebivelMes[];
  /** Soma do que já tem boleto — fato, não estimativa. */
  totalEmitido: ValorRecebivel;
  /** Soma do que é estimativa. */
  totalProjetado: ValorRecebivel;
  tarifa: TarifaVigente;
  vencimento: VencimentoInferido;
  desconto: DescontoVigente;
  abatimento: FatorAbatimento;
  /** A UC já demonstrou que fatura — ver `temFaturamentoComprovado`. */
  faturamentoComprovado: boolean;
  /** Avisos que valem para a UC inteira, não para um mês específico. */
  avisos: string[];
}

/** A menor confiança entre as que entram — a corrente vale pelo elo mais fraco. */
export function piorConfianca(...niveis: readonly ConfiancaRecebivel[]): ConfiancaRecebivel {
  const ordem: ConfiancaRecebivel[] = ['fato', 'alta', 'media', 'baixa'];
  return niveis.reduce((pior, n) => (ordem.indexOf(n) > ordem.indexOf(pior) ? n : pior), 'fato' as ConfiancaRecebivel);
}

/**
 * Monta a agenda de recebíveis de uma UC.
 *
 * A regra que sustenta o número: **uma competência que já tem boleto emitido
 * NUNCA é projetada por cima.** Medido em produção, 514 faturas já existem com
 * vencimento futuro (R$ 376 mil, 394 UCs) — só setembro/2026 tem 395 delas.
 * Projetar esses meses dobraria o valor esperado exatamente onde o Financeiro
 * mais confia no sistema.
 *
 * O corte do passado é pelo MÊS DO VENCIMENTO: a fatura de uma competência já
 * encerrada que vence no mês corrente ou depois é recebível e entra.
 *
 * @param projecaoKwh kWh projetado por competência (ordinal → kWh), vindo de
 *   `src/lib/projecaoConsumo.ts`. Sem entrada para uma competência, ela não
 *   entra na agenda — melhor faltar linha do que inventar consumo.
 */
export function montarAgendaRecebiveis({
  faturas,
  projecaoKwh,
  descontoTarifa,
  descontoBandeira,
  percentualVigor,
  competenciaAtualOrdinal,
  confiancaProjecaoConsumo = 'media',
}: {
  faturas: readonly FaturaRecebivel[];
  projecaoKwh: ReadonlyMap<number, number>;
  /** Desconto do CADASTRO. A fatura mais recente tem precedência — ver `resolverDescontoVigente`. */
  descontoTarifa: number;
  descontoBandeira: number;
  percentualVigor?: number | null;
  competenciaAtualOrdinal: number;
  confiancaProjecaoConsumo?: ConfiancaRecebivel;
}): AgendaRecebiveis {
  const ciclo = faturasDoCiclo(faturas);
  const emitidas = porCompetencia(ciclo);
  const tarifa = resolverTarifaVigente(faturas, competenciaAtualOrdinal);
  const vencimento = inferirVencimento(faturas);
  const desconto = resolverDescontoVigente(faturas, { descontoTarifa, descontoBandeira });
  const abatimento = calcularFatorAbatimento(faturas);
  const comprovado = temFaturamentoComprovado(faturas);
  const deParceira = num(percentualVigor) > 0;

  const avisos: string[] = [];
  if (tarifa.aviso) avisos.push(tarifa.aviso);
  if (vencimento.aviso) avisos.push(vencimento.aviso);
  if (desconto.aviso) avisos.push(desconto.aviso);
  if (abatimento.competenciasInconsistentes.length > 0) {
    const lista = abatimento.competenciasInconsistentes.join(', ');
    avisos.push(
      `Fatura a conferir em ${lista}: o valor do boleto não bate com a energia e a tarifa da própria ` +
        'fatura. A estimativa ignora esses meses, mas o histórico da UC está inconsistente.',
    );
  }

  const meses: RecebivelMes[] = [];

  // 1. O que já foi emitido é fato e entra como está.
  for (const [ordinal, f] of emitidas) {
    // Linha-fantasma não é fatura emitida: deixa a competência livre para ser
    // projetada em vez de valer R$ 0 calado.
    if (!temConteudo(f)) continue;
    const vencEmitido = vencimentoEfetivo(f);
    const ordinalVencEmitido = vencEmitido ? ordinalDaData(vencEmitido) : null;
    // O corte é pelo MÊS DO VENCIMENTO, não pela competência. BUG corrigido
    // (15/09/2026): cortar por competência jogava fora a fatura de AGO que vence
    // em SET — o caso de 68% da carteira —, e o mês corrente saía com 78 boletos
    // e R$ 110 mil quando o banco tinha 691 boletos e R$ 697 mil vencendo nele.
    if ((ordinalVencEmitido ?? ordinal) < competenciaAtualOrdinal) continue;
    // Em UC de parceira o boleto é único: a conta da Equatorial sai antes de
    // qualquer fatia — ver `valorSemEquatorial`.
    const valorBoleto = round2(valorSemEquatorial(f, deParceira));
    const kwh = num(f.qtd_injecao_scee_boleto);
    meses.push({
      competencia: rotuloDeOrdinal(ordinal),
      ordinal,
      origem: 'emitido',
      vencimento: vencEmitido,
      ordinalVencimento: ordinalVencEmitido,
      vencimentoEstimado: false,
      janelaDias: null,
      valorBoleto,
      valorVigor: parteDaVigor(valorBoleto, percentualVigor),
      kwh,
      // Boleto emitido: valor e data são os dele, nada é estimado.
      confianca: 'fato',
      confiancaValor: 'fato',
      confiancaData: 'fato',
      confiancaMes: 'fato',
      statusBoleto: f.status_boleto_vigor ?? null,
      faturamentoComprovado: comprovado,
      avisos: [],
    });
  }

  // 2. O que não tem boleto é projetado.
  for (const [ordinal, kwh] of projecaoKwh) {
    // ← a guarda contra o duplo contagem. A linha-fantasma não conta como
    // emitida, senão a competência ficaria sem valor nenhum.
    const jaEmitida = emitidas.get(ordinal);
    if (jaEmitida && temConteudo(jaEmitida)) continue;
    // Competência já encerrada só é projetada se a fatura dela ainda NÃO chegou.
    // Se chegou zerada ("Sem injeção"/"Sem cobrança"), o mês já tem resposta:
    // não há o que cobrar, e projetar por cima inventaria receita.
    if (ordinal < competenciaAtualOrdinal && jaEmitida) continue;

    const avisosMes: string[] = [];
    const { valorBoleto, valorVigor } = calcularValorRecebivel({
      kwh,
      tarifa: tarifa.tarifa,
      bandeira: tarifa.bandeira,
      descontoTarifa: desconto.tarifa,
      descontoBandeira: desconto.bandeira,
      percentualVigor,
      fatorAbatimento: abatimento.fator,
    });

    if (tarifa.tarifa <= 0) avisosMes.push('Sem tarifa conhecida: o valor não pôde ser estimado.');

    // O vencimento cai `lagMeses` depois da competência — não na competência.
    const ordinalVenc = ordinal + vencimento.lagMeses;
    // Mesmo corte do emitido: vencimento em mês que já passou não é recebível.
    if (ordinalVenc < competenciaAtualOrdinal) continue;
    meses.push({
      competencia: rotuloDeOrdinal(ordinal),
      ordinal,
      origem: 'projetado',
      vencimento: vencimento.dia != null ? dataDoVencimento(ordinalVenc, vencimento.dia) : null,
      ordinalVencimento: ordinalVenc,
      vencimentoEstimado: true,
      janelaDias: vencimento.janela,
      valorBoleto,
      valorVigor,
      kwh,
      // `vencimento.confiancaMes` entra aqui de propósito: `ordinalVencimento` é
      // o eixo que `agregarPorVencimento` soma, e ele é definido pelo LAG, não
      // pelo dia. Uma UC com dia perfeitamente estável (confiança 'alta') mas
      // lag inconsistente (ex.: vence ora no mesmo mês, ora no seguinte) tem o
      // MÊS do dinheiro incerto — sem esta linha o mês saía com confiança
      // 'alta' mesmo quando `ordinalVencimento` era só o lag mais frequente,
      // uma aposta de 2 em 3. Achado auditando a cobertura de teste do módulo.
      confianca: piorConfianca(tarifa.confianca, vencimento.confianca, vencimento.confiancaMes, confiancaProjecaoConsumo),
      // O VALOR não depende da data: quanto se recebe sai de tarifa × consumo.
      confiancaValor: piorConfianca(tarifa.confianca, confiancaProjecaoConsumo),
      confiancaData: vencimento.confianca,
      confiancaMes: vencimento.confiancaMes,
      statusBoleto: null,
      faturamentoComprovado: comprovado,
      avisos: avisosMes,
    });
  }

  meses.sort((a, b) => a.ordinal - b.ordinal);

  const somar = (origem: OrigemRecebivel): ValorRecebivel =>
    meses
      .filter((m) => m.origem === origem)
      .reduce(
        (acc, m) => ({ valorBoleto: round2(acc.valorBoleto + m.valorBoleto), valorVigor: round2(acc.valorVigor + m.valorVigor) }),
        { valorBoleto: 0, valorVigor: 0 },
      );

  return {
    meses,
    totalEmitido: somar('emitido'),
    totalProjetado: somar('projetado'),
    tarifa,
    vencimento,
    desconto,
    abatimento,
    faturamentoComprovado: comprovado,
    avisos,
  };
}

/** Ordinal da competência de uma data ISO — ponte entre calendário e competência. */
export function ordinalDaData(iso: string): number {
  const ano = Number(iso.slice(0, 4));
  const mes = Number(iso.slice(5, 7));
  return ano * 12 + (mes - 1);
}

export { mesReferenciaParaOrdinal, normalizarMesReferencia, rotuloDeOrdinal };
